import { useRef, useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import ReactImageGallery from "react-image-gallery";
import imgs from "./temp.json";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown,
  faBath,
  faBed,
  faClock,
  faEnvelope,
  faPhone,
  faRulerCombined,
} from "@fortawesome/free-solid-svg-icons";
import professional from "../assets/images/Smiling_Professional-min.jpg";
import constants from "../constants/constants";
import DEMO_LISTINGS from "../constants/demoListings";
import placeholder from "../assets/images/house-placeholder.webp";
import SEO, { pageSEO } from "../components/seo.component";
import isReactSnap from "../utils/isReactSnap";
import { useUserParams } from "../hooks/useUserParams";

// Function to get all images from assets/images folder based on ListingKey
function getListingImages(listingKey) {
  if (!listingKey) {
    return [{ original: placeholder, thumbnail: placeholder }];
  }
  
  try {
    // Use require.context to get all images from the images folder
    const imageContext = require.context('../assets/images', false, /\.(jpg|jpeg|png|webp)$/i);
    const imageKeys = imageContext.keys();
    
    // Find all images that contain the ListingKey in their filename
    const matchingImages = imageKeys.filter(key => {
      const fileName = key.toLowerCase();
      const listingKeyLower = listingKey.toLowerCase();
      return fileName.includes(listingKeyLower);
    });
    
    if (matchingImages.length === 0) {
      return [{ original: placeholder, thumbnail: placeholder }];
    }
    
    // Sort images: prioritize "main photo" first, then sort the rest
    const sortedImages = matchingImages.sort((a, b) => {
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();
      const aIsMain = aLower.includes('main photo');
      const bIsMain = bLower.includes('main photo');
      
      if (aIsMain && !bIsMain) return -1;
      if (!aIsMain && bIsMain) return 1;
      return a.localeCompare(b);
    });
    
    // Convert to ReactImageGallery format
    return sortedImages.map(key => {
      const imageUrl = imageContext(key);
      return {
        original: imageUrl,
        thumbnail: imageUrl,
      };
    });
  } catch (error) {
    console.error('Error loading listing images:', error);
    return [{ original: placeholder, thumbnail: placeholder }];
  }
}

export default function Listing() {
  const proxyUrl = "https://cors-anywhere.herokuapp.com/";
  const [popup, setPopup] = useState(false);
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    Name: "",
    "Email Address": "",
    Message: "",
  });
  
  // Add validation state
  const [formErrors, setFormErrors] = useState({
    Name: "",
    "Email Address": "",
    Message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage,
    getCurrentHeadshot,
  } = useUserParams();

  const agentName = getCurrentAgentName() || constants.AGENT_NAME;
  const agentTitle = getCurrentAgentTitle() || constants.AGENT_TITLE;
  const agentNumber = getCurrentAgentNumber() || constants.AGENT_NUMBER;
  const agentEmail = getCurrentAgentEmail() || constants.AGENT_EMAIL;
  const brokerage = getCurrentBrokerage() || constants.BROKERAGE;
  const headshot = getCurrentHeadshot() || constants.HEADSHOT;

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Form validation function
  const validateForm = () => {
    const errors = {};
    
    // Check if Name is empty
    if (!formData.Name.trim()) {
      errors.Name = "Name is required";
    }
    
    // Check if Email is empty and valid format
    if (!formData["Email Address"].trim()) {
      errors["Email Address"] = "Email is required";
    } else if (!validateEmail(formData["Email Address"])) {
      errors["Email Address"] = "Please enter a valid email address";
    }
    
    // Check if Message is empty
    if (!formData.Message.trim()) {
      errors.Message = "Message is required";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  async function submitForm(event) {
    console.log("submitForm");
    event.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    console.log("process.env.REACT_APP_AGENT_EMAIL");
    console.log(process.env.REACT_APP_AGENT_EMAIL);

    const emailFormData = new FormData();
    emailFormData.append('data', JSON.stringify({
      recipientEmails: agentEmail ? [agentEmail] : [],
      senderEmail: process.env.REACT_APP_AGENT_EMAIL,
      subject: "New Contact Form Message",
      content: formData.Message,
      senderName: formData.Name,
      replyTo: formData["Email Address"],
    }));

    let EmailApiUrl = process.env.REACT_APP_NODE_ENV === "production" ? "https://3.138.143.209/global/send-email" : "http://3.138.143.209/global/send-email";


    try {
      const response = await fetch(EmailApiUrl, {
        method: "POST",
        body: emailFormData,
      });

      if (!response.ok) {
        throw new Error("Failed to send email");
      }

      const res = await response.json();
      console.log("res", res);

      if (res?.message === "success") {
        console.log("Success", res);
        setPopup({
          subject: "Thanks for your message!",
          message: "We will get back to you as soon as possible",
        });
        // Clear form after successful submission
        setFormData({
          Name: "",
          "Email Address": "",
          Message: "",
        });
        // Clear errors after successful submission
        setFormErrors({
          Name: "",
          "Email Address": "",
          Message: "",
        });
      } else {
        setPopup({
          subject: "We encountered an error in sending this message",
          message: "Please try again later",
        });
      }
    } catch (err) {
      console.log(err);
      setPopup({
        subject: "We encountered an error in sending this message",
        message: "Please try again later",
      });
    } finally {
      setIsSubmitting(false);
    }
  }
  
  const { id } = useParams();
  const [property, setProperty] = useState({});
  const photos = useRef([]);
  const [media, setMedia] = useState(imgs.full.value);
  const [formatted, setFormatted] = useState({});
  const loaded = useRef(false);
  const loadedRef = useRef(false);
  const [defaultMessage, setDefaultMessage] = useState(
    `Hello, I recently came across a listing and would like more information.`
  );
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  const onWindowResize = () => {
    setWindowSize(window.innerWidth);
  };
  useEffect(() => {
    window.addEventListener("resize", onWindowResize);
    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
  }, []);

  function formatStr(str) {
    const result = str
      .replaceAll(" ", "(S)")
      .replaceAll("&", "(A)")
      .replaceAll("?", "(Q)");

    return result;
  }

  function getData() {
    var body = {
      url: `https://query.ampre.ca/odata/Property?$filter=ListingKey eq '${id}'`,
      needAuth: true,
      bearerToken: constants.TOKEN,
      // Accept: 'application/json',
      // Authorization: 'Bearer eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ2ZW5kb3IvdHJyZWIvNjUyMCIsImF1ZCI6IkFtcFVzZXJzUHJkIiwicm9sZXMiOlsiQW1wVmVuZG9yIl0sImlzcyI6InByb2QuYW1wcmUuY2EiLCJleHAiOjI1MzQwMjMwMDc5OSwiaWF0IjoxNzMwMjI1OTA1LCJzdWJqZWN0VHlwZSI6InZlbmRvciIsInN1YmplY3RLZXkiOiI2NTIwIiwianRpIjoiMGYyMjk3OTY1Y2NjN2U4MSIsImN1c3RvbWVyTmFtZSI6InRycmViIn0.BMvVrtwah8swIzW38UMs6yKF-HM4QO0bZu81blYx6J4'
    };
    return new Promise(async (resolve, reject) => {
      try {
        // let request = `odata/Property?$filter=ListingKey eq '${id}'`
        let request = `odata/Property(Q)$filter=ListingKey(S)eq(S)'${id}'`;
        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${request}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
                method: "POST",
                body: JSON.stringify(body),
                headers: {
                  "Content-Type": "application/json",
                },
              });
        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function formatMedia(media) {
    if (!media?.length) {
      return Array(6).fill({ original: placeholder, thumbnail: placeholder });
    }
    var temp1 = {};

    for (let img of media) {
      if (temp1[img.MediaObjectID]) {
        temp1[img.MediaObjectID] = {
          ...temp1[img.MediaObjectID],
          [img.ImageSizeDescription]: img,
        };
      } else {
        temp1[img.MediaObjectID] = { [img.ImageSizeDescription]: img };
      }
    }
    var temp = [];
    for (let [k, v] of Object.entries(temp1)) {
      temp.push({
        original: v?.LargestNoWatermark?.MediaURL,
        thumbnail: v?.Thumbnail?.MediaURL,
      });
    }

    return temp;
  }

  function getMedia() {
    var body = {
      url: `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey eq '${id}' and (ImageSizeDescription eq 'LargestNoWatermark' or ImageSizeDescription eq 'Thumbnail')`,
      needAuth: true,
      bearerToken: constants.TOKEN,
   };
    return new Promise(async (resolve, reject) => {
      try {
        let request = `odata/Media?$filter=ResourceRecordKey eq '${id}' and (ImageSizeDescription eq 'LargestNoWatermark' or ImageSizeDescription eq 'Thumbnail')`;
        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(
                formatStr(`https://${constants.DOMAIN}/api/${request}`)
              )
            : await fetch(`http://${constants.URL}/metaGet`, {
                method: "POST",
                body: JSON.stringify(body),
                headers: {
                  "Content-Type": "application/json",
                },
              });
        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  // Function to load property data from DEMO_LISTINGS based on ListingKey
  const loadFromDemoListings = useCallback(() => {
    const demoProperty = DEMO_LISTINGS.find((listing) => listing.ListingKey === id);
    if (demoProperty) {
      setProperty(demoProperty);
      setFormData((prev) => ({
        ...prev,
        Message: `Hello, I recently came across a listing at ${
          demoProperty?.StreetNumber ?? "--"
        } ${demoProperty?.StreetName ?? "--"} ${
          demoProperty?.StreetSuffix ?? ""
        } and would like more information.`,
      }));
      // Load images from images folder based on ListingKey
      setImages(getListingImages(id));
    } else {
      console.log(`No listing found with ListingKey: ${id}`);
      // Set empty property if not found
      setProperty({});
      setImages(getListingImages(id));
    }
  }, [id]);

  useEffect(() => {
    if (isReactSnap()) return; // Skip fetching during react-snap
    
    // Load data when id changes
    getData()
      .then((data) => {
        if (data.value && data.value.length > 0) {
          setProperty(data.value[0]);
          setFormData((prev) => ({
            ...prev,
            Message: `Hello, I recently came across a listing at ${
              data.value[0]?.StreetNumber ?? "--"
            } ${data.value[0]?.StreetName ?? "--"} ${
              data.value[0]?.StreetSuffix ?? ""
            } and would like more information.`,
          }));
        } else {
          // If API returns empty data, try to load from DEMO_LISTINGS
          loadFromDemoListings();
        }
      })
      .catch((err) => {
        console.log("API fetch failed, trying to load from DEMO_LISTINGS:", err);
        // If API fails, try to load from DEMO_LISTINGS
        loadFromDemoListings();
      });
    
    // Load images from images folder based on ListingKey
    setImages(getListingImages(id));

    loaded.current = true;
    loadedRef.current = true;
  }, [id, loadFromDemoListings]);
  const [images, setImages] = useState([]);

  // useEffect(() => {
  //     var temp = []
  //     for (let img of Object.values(formatted)) {
  //         for (let [k, v] of Object.entries(img)) {
  //             temp.push({ original: v?.LargestNoWatermark?.MediaURL, thumbnail: v?.Thumbnail?.MediaURL })

  //         }

  //     }
  //     setImages(temp)
  //     console.log(temp)
  // }, [formatted])

  const [detailsOpen, setDetailsOpen] = useState(false);

  const seoConfig = pageSEO.listing(property);

  return (
    <>
      <SEO {...seoConfig}>
        {/* Add structured data as children */}
        {seoConfig.structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(seoConfig.structuredData)}
          </script>
        )}
      </SEO>
      <div className="max-w-[1400px] mx-auto px-8 sm:w-[75vw] pt-[100px]">
        <div className="flex justify-between pt-8 pb-6 items-end gap-x-10 gap-y-6 flex-wrap">
          <div>
            <h1 className="text-4xl text-black libre ">
              {property?.StreetNumber} {property?.StreetName}{" "}
              {property?.StreetSuffix}
            </h1>
            <p className="text-med-grey text-2xl mt-2">
              {property?.City} {property?.StateOrProvince},{" "}
              {property?.PostalCode}
            </p>
          </div>
          <div className="flex flex-col lg:items-end whitespace-nowrap">
            <h1 className="font-semibold text-xl text-black">
              <span className="text-logo-second">Listing Price:</span>{" "}
              {property?.ListPrice?.toLocaleString()}
            </h1>
            <p className="font-semibold mt-2 text-med-grey text-lg">
              {" "}
              <FontAwesomeIcon
                className="text-logo-second mr-1"
                icon={faClock}
              />{" "}
              Listed on{" "}
              {property?.OriginalEntryTimestamp
                ? new Date(property?.OriginalEntryTimestamp).toLocaleString(
                    "default",
                    { month: "short", day: "numeric", year: "numeric" }
                  )
                : "--"}
            </p>
          </div>
        </div>
        <div className="flex gap-x-8 justify-between h-fit">
          <div className="min-[1201px]:w-[80%]">
            <ReactImageGallery
              items={images}
              loading={"eager"}
              thumbnailPosition={`${windowSize > 950 ? "right" : "bottom"}`}
              showThumbnails={windowSize > 950 ? true : false}
              showPlayButton={false}
            />
            <div className="w-full px-3 py-2 bg-logo-second mt-5">
              <span className="text-white font-semibold ">
                Property Features
              </span>
            </div>
            <div className="grid mt-5 gap-x-10 gap-y-5 w-[75%] sm:grid-cols-2">
              <div className="flex gap-x-3 items-end">
                <FontAwesomeIcon
                  icon={faBed}
                  className="text-logo-second fa-2xl"
                />
                <span className=" text-xl font-semibold text-med-grey">
                  {property?.BedroomsTotal
                    ? property?.BedroomsTotal + " Bedrooms"
                    : "--"}
                </span>
              </div>
              <div className="flex gap-x-3 items-end">
                <FontAwesomeIcon
                  icon={faBath}
                  className="text-logo-second fa-2xl"
                />
                <span className=" text-xl font-semibold text-med-grey">
                  {property?.BathroomsTotalInteger
                    ? property?.BathroomsTotalInteger + " Bathrooms"
                    : "--"}
                </span>
              </div>
              <div className="flex gap-x-3 items-end">
                <FontAwesomeIcon
                  icon={faRulerCombined}
                  className="text-logo-second fa-2xl"
                />
                <span className=" text-xl font-semibold text-med-grey">
                  {property?.LivingAreaRange
                    ? property?.LivingAreaRange + " Sq Ft"
                    : property?.OfficeApartmentArea
                    ? property?.OfficeApartmentArea + " Sq Ft"
                    : property?.BuildingAreaTotal
                    ? property.BuildingAreaTotal
                    : "--"}
                </span>
              </div>
            </div>

            <h1 className="text-4xl text-black libre mt-12">
              About This Property
            </h1>
            <p
              className={`mt-6 overflow-hidden  ${
                !detailsOpen && property?.PublicRemarks?.length > 750
                  ? "masked max-h-44"
                  : ""
              }`}
            >
              {property?.PublicRemarks}
            </p>
            {property?.PublicRemarks?.length > 750 && !detailsOpen ? (
              <button
                onClick={() => setDetailsOpen(!detailsOpen)}
                className="bg-light-grey text-med-grey hover:shadow-md hover:text-black duration-point-three rounded-lg px-3 py-1.5 cursor-pointer shadow-sm mx-auto mt-3 w-fit block"
              >
                Show {detailsOpen ? "Less" : "More"}{" "}
                <FontAwesomeIcon
                  transform={{ rotate: detailsOpen ? 180 : 0 }}
                  icon={faAngleDown}
                />
              </button>
            ) : null}
            {/* {property?.PublicRemarksExtras ? */}

            <h1 className="text-4xl text-black libre mt-12">Additional Info</h1>

            <div className="grid md:grid-cols-2 mt-5 gap-x-10 gap-y-5">
              <div className="flex gap-x-3 ">
                <span className="text-black text-lg  font-semibold">
                  Property Type:{" "}
                </span>
                <span className=" text-lg text-med-grey">
                  {property?.PropertyType ?? "--"}
                </span>
              </div>
              <div className="flex gap-x-3 ">
                <span className="text-black text-lg  font-semibold">
                  Sub-Type:{" "}
                </span>
                <span className=" text-lg text-med-grey">
                  {property?.PropertySubType ?? "--"}
                </span>
              </div>
              <div className="flex gap-x-3 ">
                <span className="text-black text-lg  font-semibold">
                  Heating:{" "}
                </span>
                <span className=" text-lg text-med-grey">
                  {property?.HeatSource ?? "--"}
                </span>
              </div>
              <div className="flex gap-x-3 ">
                <span className="text-black text-lg  font-semibold">
                  HVAC:{" "}
                </span>
                <span className=" text-lg text-med-grey">
                  {property?.HeatType ?? "--"}
                </span>
              </div>
              <div className="flex gap-x-3 ">
                <span className="text-black text-lg  font-semibold">
                  Garage:{" "}
                </span>
                <span className=" text-lg text-med-grey">
                  {property?.GarageType ?? "--"}
                </span>
              </div>
              <div className="flex gap-x-3 ">
                <span className="text-black text-lg  font-semibold">
                  Parking:{" "}
                </span>
                <span className=" text-lg text-med-grey">
                  {property?.ParkingTotal ?? "--"}
                </span>
              </div>
              <div className="flex gap-x-3 ">
                <span className="text-black text-lg  font-semibold">
                  Exposure:{" "}
                </span>
                <span className=" text-lg text-med-grey">
                  {property?.DirectionFaces ?? "--"}
                </span>
              </div>
              <div className="flex gap-x-3 ">
                <span className="text-black text-lg  font-semibold">Lot: </span>
                <span className=" text-lg text-med-grey">
                  {property?.LotSizeRangeAcres ?? "--"} {property?.LotSizeUnits}
                </span>
              </div>
              <div className="flex gap-x-3 ">
                <span className="text-black text-lg  font-semibold">
                  Basement:{" "}
                </span>
                <span className=" text-lg text-med-grey">
                  {property?.Basement?.length
                    ? property.Basement.join(", ")
                    : "--"}
                </span>
              </div>
              <div className="flex gap-x-3 ">
                <span className="text-black text-lg  font-semibold">
                  Office:{" "}
                </span>
                <span className="text-xs text-med-grey">
                  {property?.ListOfficeName ?? "--"}
                </span>
              </div>
              <div className="flex gap-x-3 ">
                <span className="text-black text-lg  font-semibold">
                  Board:{" "}
                </span>
                <span className="text-xs text-med-grey">
                  {property?.ListAOR ?? "--"}
                </span>
              </div>
            </div>
            <p className="mt-6">{property?.PublicRemarksExtras}</p>

            <h1 className="text-4xl text-black libre mt-12">
              Affordability Calculator
            </h1>

            <iframe
              key={property?.ListPrice || id}
              className="mb-10 md:-ml-[4.5rem] -ml-[1rem] sm:-ml-[3rem] min-[1109px]:-ml-[3rem] min-[1430px]:-ml-[6rem] min-[1552px]:-ml-[4rem] min-[1710px]:-ml-[7rem] mt-6"
              src={`https://www.mortgagecalculator.org/webmasters/?downpayment=${Math.round((property?.ListPrice || 0) * 0.2)}&homevalue=${property?.ListPrice}&loanamount=${Math.round((property?.ListPrice || 0) * 0.8)}&interestrate=7&loanterm=30&propertytax=${Math.round((property?.ListPrice || 0) * 0.00675)}&pmi=1&homeinsurance=${Math.round((property?.ListPrice || 0) * 0.00375)}&monthlyhoa=0`}
              scrolling="no"
              style={{
                width: "100%",
                height: windowSize > 852 ? "1200px" : "2300px",
                border: 0,
              }}
            ></iframe>
          </div>

          <div className="sticky top-4 min-w-[300px] h-fit max-[1200px]:hidden mb-10 ">
            <div className="diffuse-shadow rounded-lg py-6 px-4 h-fit bg-[#f6ffa7] ">
              <h4 className=" font-semibold text-black text-lg text-center">
                Presented By
              </h4>
              <div
                style={{ overflowClipMargin: "unset" }}
                className="rounded-full w-44 h-44 overflow-clip mx-auto my-6"
              >
                <img
                  className="w-full h-full object-cover"
                  src={headshot}
                  alt="Agent"
                />
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900">{agentName}</h3>
                  <p className="text-gray-700">{agentTitle}</p>
                  <p className="text-gray-600">{brokerage}</p>
                </div>
              </div>
              <div className="flex flex-col gap-2 text-gray-700">
                <p>
                  <FontAwesomeIcon icon={faPhone} className="text-blue-800 fa-lg" /> {" "}
                  {agentNumber}
                </p>
                <p>
                  <FontAwesomeIcon icon={faEnvelope} className="text-blue-800 fa-lg" /> {" "}
                  {agentEmail}
                </p>
              </div>
            </div>
            <div className="diffuse-shadow rounded-lg py-6 px-4 h-fit mt-4 bg-[#f6ffa7]">
              <h4 className=" font-semibold text-black text-lg text-center">
                Inquire About This Listing
              </h4>
              <form
                ref={formRef}
                onSubmit={submitForm}
                className="mt-6 px-3 flex flex-col"
              >
                <div className="form-item">
                  <label className="form-label">Name*</label>
                  <input
                    className="form-input"
                    name="Name"
                    placeholder="Full Name"
                    value={formData.Name}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, Name: e.target.value }));
                      // Clear error when user starts typing
                      if (formErrors.Name) {
                        setFormErrors(prev => ({ ...prev, Name: "" }));
                      }
                    }}
                  ></input>
                  {formErrors.Name && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.Name}</p>
                  )}
                </div>
                <div className="form-item">
                  <label className="form-label">Email*</label>
                  <input
                    className="form-input"
                    name="Email Address"
                    placeholder="Email Address"
                    value={formData["Email Address"]}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        "Email Address": e.target.value,
                      }));
                      // Clear error when user starts typing
                      if (formErrors["Email Address"]) {
                        setFormErrors(prev => ({ ...prev, "Email Address": "" }));
                      }
                    }}
                  ></input>
                  {formErrors["Email Address"] && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors["Email Address"]}
                    </p>
                  )}
                </div>
                <div className="form-item">
                  <label className="form-label">Message*</label>
                  <textarea
                    className="form-input resize-none h-32"
                    name="Message"
                    value={formData.Message}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        Message: e.target.value,
                      }));
                      // Clear error when user starts typing
                      if (formErrors.Message) {
                        setFormErrors(prev => ({ ...prev, Message: "" }));
                      }
                    }}
                  ></textarea>
                  {formErrors.Message && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.Message}
                    </p>
                  )}
                </div>
                <button 
                  type="submit" 
                  className="btn-tertiary self-end"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      {popup && (
        <>
          <div className="backdrop" />
          <div className="popup">
            <h5 className="font-bold text-black text-lg">{popup.subject}</h5>
            <p className="font-semibold text-med-grey tracking-wide mb-3">
              {popup.message}
            </p>
            <button onClick={() => setPopup(false)} className="btn-tertiary">
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
